import type { AttemptView, ItemView, LearnerClient } from "@tutor/live";
import { MAX_ANSWER, normalizeAnswer, type ItemHint, type ItemSolution } from "@tutor/protocol";
import { useEffect, useId, useState, type SubmitEvent } from "react";
import { useT, type MessageKey } from "../i18n";
import { PromptText, promptSize } from "./PromptText";
import { LessonAgain } from "./LessonBoard";
import { LessonCheck } from "./LessonCheck";
import { TeachBack } from "./TeachBack";

// The exercise on the canvas, rendered from `item.presented`. The verdict shown first is
// the local one (salted digest, no network); the actor's grade replaces it when it
// arrives and always wins. The engine, never this card, decides what comes next.

type Verdict =
  | { readonly kind: "none" }
  | { readonly kind: "checking" }
  | { readonly kind: "correct"; readonly rechecked: boolean }
  | { readonly kind: "wrong"; readonly rechecked: boolean };

export function verdictOf(attempt: AttemptView | undefined): Verdict {
  if (attempt === undefined) return { kind: "none" };
  if (attempt.server !== null) {
    // The server's grade counts. Say so quietly when it disagrees with the instant check.
    const rechecked =
      attempt.local !== "unknown" && (attempt.local === "correct") !== attempt.server;
    return { kind: attempt.server ? "correct" : "wrong", rechecked };
  }
  if (attempt.local === "unknown") return { kind: "checking" };
  return { kind: attempt.local, rechecked: false };
}

/**
 * The lesson before the exercise is under way (ADR-0065): the board shows Coach's lesson
 * and « Je vérifie », and holds the exercise back until the lesson is done.
 */
export function lessonFirst(item: ItemView): boolean {
  return !item.done && item.lesson !== undefined && !item.lesson.done;
}

/** Locked while the child's last answer is right (or being checked) and when done. */
export function itemLocked(item: ItemView): boolean {
  const verdict = verdictOf(item.attempts.at(-1));
  return item.done || verdict.kind === "correct" || verdict.kind === "checking";
}

export function ItemCard(props: {
  item: ItemView;
  upNext: ItemView | null;
  client: Pick<LearnerClient, "answer" | "advance" | "explain" | "checkLesson" | "skipLesson">;
  compact?: boolean;
  /** The answer box's text when the screen shares it (the workbench follows it). */
  draft?: Draft;
}) {
  const { item, upNext, client, compact = false, draft } = props;
  const t = useT();
  const promptId = useId();
  const last = item.attempts.at(-1);
  const verdict = verdictOf(last);
  const locked = itemLocked(item);
  // The actor closed the item on a wrong answer: every try is used. Say so, or the locked
  // choices and "try a smaller step" read as an app that stopped listening (ADR-0044 point 9).
  const outOfTries = item.done && verdict.kind === "wrong";
  // The lesson Coach taught on this exercise, folded above it once the child is on it.
  const lessonPage = item.lesson?.page ?? null;

  const submit = (answer: string) => {
    if (locked) return;
    void client.answer(item.itemId, answer);
  };

  return (
    <article
      className={compact ? "item-card compact" : "item-card"}
      aria-labelledby={lessonFirst(item) ? undefined : promptId}
      data-item-id={item.itemId}
    >
      <MomentKicker item={item} found={item.done && verdict.kind === "correct"} />
      {!item.done && <LessonCheck item={item} client={client} />}
      {!lessonFirst(item) && (
        <>
          {lessonPage !== null && !item.done && <LessonAgain page={lessonPage} />}
          <h2 id={promptId} className="item-prompt" data-size={promptSize(item.prompt)}>
            <PromptText text={item.prompt} />
          </h2>

          <BoardSteps item={item} />

          {item.answer.kind === "choice" ? (
            <ChoiceAnswer
              choices={item.answer.choices}
              attempts={item.attempts}
              disabled={locked}
              labelledBy={promptId}
              shortcuts
              answerIndex={item.solution === undefined ? null : Number(item.solution.answer)}
              onPick={(index) => {
                submit(String(index));
              }}
            />
          ) : (
            <TypedAnswer
              kind={item.answer.kind}
              unit={item.answer.kind === "number" ? item.answer.unit : null}
              maxLength={item.answer.kind === "text" ? item.answer.maxLength : MAX_ANSWER}
              // After a reload the card shows what the child last wrote, not an empty box.
              initial={last?.answer ?? ""}
              draft={draft}
              disabled={locked}
              onSubmit={submit}
            />
          )}

          {item.answer.kind !== "choice" && <TriesRow attempts={item.attempts} />}

          <VerdictLine verdict={verdict} tries={item.attempts.length} outOfTries={outOfTries} />

          {!item.done &&
            verdict.kind === "wrong" &&
            item.hint !== undefined &&
            item.coachAnswered !== true && <HintNote hint={item.hint} />}

          {item.solution !== undefined && <SolutionNote item={item} solution={item.solution} />}

          {item.done && <TeachBack item={item} client={client} />}

          {item.done &&
            (upNext !== null ? (
              <button
                type="button"
                className="button primary next"
                onClick={() => {
                  client.advance();
                }}
                // The next item is already on the device: moving on is instant.
                autoFocus
              >
                {t("item.next")} <span aria-hidden="true">→</span>
              </button>
            ) : (
              <p className="item-waiting" role="status">
                {t(outOfTries ? "item.waitingNextAfterTries" : "item.waitingNext")}
              </p>
            ))}
        </>
      )}
    </article>
  );
}

/**
 * The moment above the exercise (« Deux feuilles »): the engine's `moment` for the item,
 * or « Trouvé » once the actor graded it right.
 */
function MomentKicker({ item, found }: { item: ItemView; found: boolean }) {
  const t = useT();
  const moment = found ? "found" : item.moment;
  return (
    <p className="sheet-kicker" data-moment={moment}>
      {t(`board.moment.${moment}`)}
    </p>
  );
}

/**
 * Steps Coach wrote on the board (ADR-0052, step B2), numbered, the latest one lit. While
 * the exercise is open, a faint line says the next step comes after the child's reply.
 */
function BoardSteps({ item }: { item: ItemView }) {
  const t = useT();
  if (item.steps.length === 0) return null;
  const last = item.steps.length - 1;
  return (
    <ol className="board-steps" aria-label={t("board.steps.label")}>
      {item.steps.map((step, i) => (
        <li key={step.turnId} data-now={!item.done && i === last}>
          <span className="board-step-n" aria-hidden="true">
            {i + 1}
          </span>
          <span className="board-step-text">{step.text}</span>
        </li>
      ))}
      {!item.done && (
        <li className="board-step-next" aria-hidden="true">
          <span className="board-step-n">{item.steps.length + 1}</span>
          <span className="board-step-text">{t("board.steps.next")}</span>
        </li>
      )}
    </ol>
  );
}

/** The child's tries on a typed answer, in order: a wrong one boxed, a right one marked. */
function TriesRow({ attempts }: { attempts: readonly AttemptView[] }) {
  const t = useT();
  const shown = attempts.filter((a) => a.answer !== "");
  if (shown.length < 2) return null;
  return (
    <ol className="tries-row">
      {shown.map((attempt, i) => {
        const kind = verdictOf(attempt).kind;
        return (
          <li key={attempt.clientMsgId} data-verdict={kind}>
            {t("board.tryN", { n: i + 1 })} <span className="tries-answer">{attempt.answer}</span>{" "}
            <span className="visually-hidden">
              {t(kind === "correct" ? "board.tryRight" : "board.tryNotYet")}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export const CHOICE_KEYS = "ABCDEFGH";

/** Past this many characters a choice is a sentence, and the choices stack one per row. */
export const LONG_CHOICE = 24;

/**
 * The item's choices as buttons; also used by the ChoiceQuestion widget in the chat.
 * Each choice carries a letter so a child can say "I think B" to Coach. With `shortcuts`
 * (the card on the canvas, never a widget in the chat), keys 1 to n pick a choice.
 */
export function ChoiceAnswer(props: {
  choices: readonly string[];
  attempts: readonly AttemptView[];
  disabled: boolean;
  labelledBy: string;
  shortcuts?: boolean;
  /** The right choice, shown once the item ended on a wrong answer. */
  answerIndex?: number | null;
  onPick: (index: number) => void;
}) {
  const { choices, disabled, onPick, shortcuts = false, answerIndex = null } = props;
  const last = props.attempts.at(-1);
  const tried = new Set(props.attempts.map((a) => a.answer));

  useEffect(() => {
    if (!shortcuts || disabled) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      if (typing(event.target)) return;
      const index = Number.parseInt(event.key, 10) - 1;
      if (Number.isInteger(index) && index >= 0 && index < choices.length) {
        event.preventDefault();
        onPick(index);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [shortcuts, disabled, choices.length, onPick]);

  return (
    <div
      className="choice-grid"
      role="group"
      aria-labelledby={props.labelledBy}
      data-count={choices.length}
      data-long={choices.some((c) => c.length > LONG_CHOICE) ? "true" : undefined}
    >
      {choices.map((choice, index) => {
        const value = String(index);
        const isLast = last?.answer === value;
        const lastVerdict = isLast ? verdictOf(last).kind : "none";
        return (
          <button
            key={index}
            type="button"
            className="choice"
            data-verdict={lastVerdict}
            data-tried={tried.has(value) && !isLast ? "true" : undefined}
            data-answer={answerIndex === index ? "true" : undefined}
            aria-pressed={isLast}
            aria-keyshortcuts={shortcuts ? String(index + 1) : undefined}
            disabled={disabled}
            onClick={() => {
              onPick(index);
            }}
          >
            <span className="choice-key" aria-hidden="true">
              {CHOICE_KEYS.charAt(index)}
            </span>
            <span className="choice-text">{choice}</span>
          </button>
        );
      })}
    </div>
  );
}

/** True when a key press is text entry (the answer box, Coach's composer), not a shortcut. */
function typing(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable ||
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement)
  );
}

/** A controlled answer box: its text and how to change it. */
export interface Draft {
  readonly value: string;
  readonly onChange: (value: string) => void;
}

function TypedAnswer(props: {
  kind: "number" | "text";
  unit: string | null;
  maxLength: number;
  initial: string;
  draft?: Draft | undefined;
  disabled: boolean;
  onSubmit: (answer: string) => void;
}) {
  const t = useT();
  const inputId = useId();
  const hintId = useId();
  const [local, setLocal] = useState(props.initial);
  const draft = props.draft?.value ?? local;
  const setDraft = props.draft?.onChange ?? setLocal;
  const readable = normalizeAnswer(props.kind, draft) !== null;
  const showHint = props.kind === "number" && draft.trim() !== "" && !readable;

  const onSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!readable || props.disabled) return;
    props.onSubmit(draft.trim());
  };

  return (
    <form className="typed-answer" onSubmit={onSubmit} noValidate>
      <label htmlFor={inputId} className="visually-hidden">
        {t("item.yourAnswer")}
      </label>
      <div className="answer-row">
        <input
          id={inputId}
          className="answer-input"
          type="text"
          inputMode={props.kind === "number" ? "decimal" : "text"}
          enterKeyHint="done"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          autoCapitalize="off"
          maxLength={props.maxLength}
          placeholder={props.kind === "text" ? t("item.textPlaceholder") : "?"}
          aria-describedby={props.kind === "number" ? hintId : undefined}
          aria-invalid={showHint}
          value={draft}
          disabled={props.disabled}
          onChange={(event) => {
            setDraft(event.target.value);
          }}
        />
        {props.unit !== null && <span className="answer-unit">{props.unit}</span>}
        <button type="submit" className="button primary" disabled={props.disabled || !readable}>
          {t("item.check")}
        </button>
      </div>
      {props.kind === "number" && (
        <p id={hintId} className={showHint ? "answer-hint unreadable" : "answer-hint"}>
          {t("item.numberHint")}
        </p>
      )}
    </form>
  );
}

// `tries` already counts the answer being shown: 1 is a first try. The "More" lines
// speak to a later try ("Still not there", "You kept at it"), so never show them first.

function VerdictLine({
  verdict,
  tries,
  outOfTries,
}: {
  verdict: Verdict;
  tries: number;
  outOfTries: boolean;
}) {
  const t = useT();
  if (verdict.kind === "none") return <p className="verdict" role="status" aria-live="polite" />;
  let key: MessageKey;
  switch (verdict.kind) {
    case "checking":
      key = "item.checking";
      break;
    case "correct":
      key = verdict.rechecked
        ? "item.recheckedCorrect"
        : tries <= 1
          ? "item.correct"
          : "item.correctMore";
      break;
    case "wrong":
      key = outOfTries
        ? "item.outOfTries"
        : verdict.rechecked
          ? "item.recheckedWrong"
          : tries <= 1
            ? "item.wrong"
            : "item.wrongMore";
      break;
  }
  const quiet = verdict.kind !== "checking" && verdict.rechecked;
  return (
    <p
      // A new key per try replays the gentle animation for each new verdict.
      key={`${verdict.kind}-${String(tries)}`}
      className={`verdict ${verdict.kind}${quiet ? " quiet" : ""}`}
      data-verdict={verdict.kind}
      role="status"
      aria-live="polite"
    >
      {verdict.kind === "correct" && !quiet && <Sparkles />}
      {t(key)}
    </p>
  );
}

/**
 * The bank's hint at the level the engine allows after a wrong try, so the next step
 * is on the card without asking Coach. Keyed by level so a new hint replays the animation.
 */
function HintNote({ hint }: { hint: ItemHint }) {
  const t = useT();
  return (
    <p key={hint.level} className="item-hint" data-level={hint.level}>
      <span className="item-hint-label">{t("item.hintLabel")}</span> {hint.text}
    </p>
  );
}

/** The right answer and the bank's worked solution, after every try was used. */
export function SolutionNote({ item, solution }: { item: ItemView; solution: ItemSolution }) {
  const t = useT();
  const index = Number(solution.answer);
  const answer =
    item.answer.kind === "choice"
      ? `${CHOICE_KEYS.charAt(index)}. ${item.answer.choices[index] ?? ""}`
      : solution.answer;
  return (
    <div className="item-solution">
      <p className="item-solution-answer">{t("item.rightAnswer", { answer })}</p>
      <p className="item-solution-text">{solution.explanation}</p>
    </div>
  );
}

function Sparkles() {
  return (
    <span className="sparkles" aria-hidden="true">
      {Array.from({ length: 6 }, (_, i) => (
        <span key={i} className="sparkle" style={{ ["--i" as string]: i }} />
      ))}
    </span>
  );
}
